import { useState } from "react";
import type { DetailType } from "#shared/schemas/entityDetail.ts";
import type { MbLinkEntityType } from "#shared/schemas/mbEnrich.ts";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { JsonTree } from "@web/features/settings/JsonTree.tsx";
import { AbPrettyView } from "@web/features/detail/AbPrettyView.tsx";
import { MbPrettyView } from "@web/features/detail/MbPrettyView.tsx";
import { useMbLink } from "@web/features/settings/mbApi.ts";
import { formatDateTime } from "@web/lib/format.ts";

function asLinkType(type: DetailType): MbLinkEntityType | null {
  if (type === "artist" || type === "track" || type === "album") return type;
  return null;
}

function mbPage(type: string, mbid: string): string {
  return `https://musicbrainz.org/${type}/${mbid}`;
}

/**
 * User-facing pretty explorer for stored MusicBrainz / AcousticBrainz JSON.
 * Workbench dump / manual MBID stay admin-only.
 */
export function MetaSection({
  entityType,
  entityId,
}: {
  entityType: DetailType;
  entityId: string;
}) {
  const linkType = asLinkType(entityType);
  const linkQ = useMbLink(linkType, entityId);
  const [rawOpen, setRawOpen] = useState(false);

  if (!linkType) {
    return (
      <p className="text-sm text-muted">
        Meta is available for tracks, albums, and artists.
      </p>
    );
  }

  if (linkQ.isLoading) return <Skeleton className="h-32 w-full" />;

  const link = linkQ.data?.link ?? null;
  const entity = linkQ.data?.entity ?? null;
  const acousticbrainz = linkQ.data?.acousticbrainz ?? null;

  if (!entity || !link?.mbid) {
    return (
      <p className="text-sm text-muted">
        No stored metadata yet. Use Fetch data on Overview to fill missing
        enrichment for this item.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            MusicBrainz
          </p>
          <a
            href={mbPage(entity.entity_type, link.mbid)}
            target="_blank"
            rel="noreferrer"
            className="mt-1 block truncate text-sm text-accent hover:underline"
          >
            {link.mbid}
          </a>
          <p className="mt-1 text-xs text-muted">
            {entity.fetch_status}
            {entity.fetched_at ? ` · ${formatDateTime(entity.fetched_at)}` : ""}
            {link.match_method ? ` · ${link.match_method}` : ""}
          </p>
        </div>
        <div className="rounded-md border border-line bg-panel px-3 py-2">
          <MbPrettyView
            raw={entity.raw_json}
            entityType={entity.entity_type}
            mbid={link.mbid}
          />
        </div>
        <div className="rounded-md border border-line bg-panel">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
            onClick={() => setRawOpen((v) => !v)}
          >
            <span className="text-xs font-semibold tracking-wider text-faint uppercase">
              Raw JSON
            </span>
            <span className="text-xs text-muted">{rawOpen ? "Hide" : "Show"}</span>
          </button>
          {rawOpen && (
            <div className="max-h-[28rem] overflow-auto border-t border-line px-3 py-2">
              <JsonTree value={entity.raw_json} name={entity.entity_type} />
            </div>
          )}
        </div>
      </section>

      {linkType === "track" && acousticbrainz ? (
        <AbPrettyView entity={acousticbrainz} />
      ) : null}
    </div>
  );
}
